import { memo, useState } from "react";

/**
 * Length at which the description collapses behind a disclosure. The full text
 * stays reachable — an error nobody can read in full is an error nobody can
 * report — but a chart should not open as a log viewer.
 */
const MAX_DETAIL_LENGTH = 180;

interface ChartErrorProps {
  /** One short line naming what could not be shown. */
  title?: string;
  /** Underlying error text, shown as the description. Truncated if long. */
  detail?: string | null;
  /** Renders a Retry action. Omit when the caller has nothing to re-run. */
  onRetry?: () => void;
  height?: number;
}

/**
 * Failure counterpart to ChartPlaceholder, shown when a chart's data could not
 * be loaded. Mirrors ChartPlaceholder's chart-frame structure so a chart moving
 * between loading, error and loaded states keeps the same footprint and the page
 * does not jump.
 *
 * Follows the design system's empty-state pattern (icon → title → description →
 * action). Everything is monochrome except the icon, which carries the error
 * status colour — per the rule that status colours belong on icons and text,
 * never decoratively.
 */
function ChartError({ title = "Chart unavailable", detail, onRetry, height = 400 }: ChartErrorProps) {
  const [expanded, setExpanded] = useState(false);

  const full = detail?.trim() ? detail.trim() : null;
  const isTruncated = full !== null && full.length > MAX_DETAIL_LENGTH;
  const shown = isTruncated && !expanded ? `${full.slice(0, MAX_DETAIL_LENGTH)}…` : full;

  return (
    <div className="chart-frame">
      <div className="chart-frame__header">
        <h2>&nbsp;</h2>
      </div>
      <div className="chart-error" style={{ height }} role="status">
        <div className="chart-error__body">
          <WarningIcon />
          <h4 className="chart-error__title">{title}</h4>
          {shown && <p className={`chart-error__detail${expanded ? " chart-error__detail--expanded" : ""}`}>{shown}</p>}
          {isTruncated && (
            <button
              type="button"
              className="btn-naked chart-error__disclosure"
              aria-expanded={expanded}
              onClick={() => setExpanded(value => !value)}
            >
              {expanded ? "Show less" : "Show full message"}
            </button>
          )}
          {onRetry && (
            <button type="button" className="btn-ghost chart-error__action" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** 32px monochrome line icon, per the design system's icon rules. */
function WarningIcon() {
  return (
    <svg
      className="chart-error__icon"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="16" cy="16" r="12" />
      <path d="M16 10v8" strokeLinecap="round" />
      <path d="M16 22h0" strokeLinecap="round" />
    </svg>
  );
}

export default memo(ChartError);
